import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api, type BankAccount, type Beneficiary, type OrderDetail, type OrderSummary, type Page,
} from '../shared/api';
import {
  Empty, ErrorNote, Modal, PlusIcon, RiskPill, Skeleton, StatePill,
  formatDate, relativeTime, titleise, useDebounced, useToast,
} from '../shared/ui';

const STATES = [
  'DRAFT', 'IN_APPROVAL', 'APPROVED', 'PUBLISHED', 'EXECUTING', 'SETTLED', 'REJECTED',
] as const;

const PURPOSES = [
  'SALARY', 'PURCHASE', 'OFFICE_EXPENSE', 'REIMBURSEMENT', 'DISINVESTMENT', 'CASH_RECEIVE',
] as const;

export function OrdersPage() {
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [state, setState] = useState('');
  const [purpose, setPurpose] = useState('');
  const [page, setPage] = useState(1);
  const debounced = useDebounced(search, 300);
  const showCreate = params.get('new') === '1';

  const query = new URLSearchParams({ page: String(page), page_size: '25' });
  if (state) query.set('state', state);
  if (purpose) query.set('purpose', purpose);
  if (debounced) query.set('q', debounced);

  const { data, isLoading, error } = useQuery({
    queryKey: ['orders', query.toString()],
    queryFn: () => api.get<Page<OrderSummary>>(`/orders?${query}`),
  });

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Ledger</div>
          <h1 className="page-title">Payment orders</h1>
          <p className="page-sub">
            {data ? `${data.total} order${data.total === 1 ? '' : 's'}` : 'Loading…'}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setParams({ new: '1' })}>
          <PlusIcon /> New payment order
        </button>
      </div>

      <div className="filter-bar card row row-wrap gap-10" style={{ marginBottom: 14, padding: 13 }}>
        <input
          className="input grow filter-search"
          placeholder="Search voucher number or note…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          aria-label="Search payment orders"
        />
        <select
          className="select filter-select" value={state}
          onChange={(e) => { setState(e.target.value); setPage(1); }}
          aria-label="Filter by state"
        >
          <option value="">All states</option>
          {STATES.map((s) => <option key={s} value={s}>{titleise(s)}</option>)}
        </select>
        <select
          className="select filter-select" value={purpose}
          onChange={(e) => { setPurpose(e.target.value); setPage(1); }}
          aria-label="Filter by purpose"
        >
          <option value="">All purposes</option>
          {PURPOSES.map((p) => <option key={p} value={p}>{titleise(p)}</option>)}
        </select>
        {(state || purpose || search) && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => { setState(''); setPurpose(''); setSearch(''); setPage(1); }}
          >
            Clear
          </button>
        )}
      </div>

      {error && <ErrorNote error={error} />}
      {isLoading ? (
        <Skeleton rows={8} />
      ) : !data || data.items.length === 0 ? (
        <div className="card">
          <Empty
            title="Nothing here"
            hint={
              search || state || purpose
                ? 'No payment orders match these filters. Try clearing them.'
                : 'Create a payment order to start the approval workflow.'
            }
            action={
              <button className="btn btn-primary" onClick={() => setParams({ new: '1' })}>
                Create a payment order
              </button>
            }
          />
        </div>
      ) : (
        <>
          <div className="card card-flush">
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Voucher</th>
                    <th className="hide-mobile">Purpose</th>
                    <th className="hide-mobile">Value date</th>
                    <th className="right">Amount</th>
                    <th className="hide-mobile">Lines</th>
                    <th className="hide-mobile">Approval</th>
                    <th>State</th>
                    <th className="hide-mobile">Risk</th>
                    <th className="hide-mobile">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((o) => (
                    <OrderRow key={o.id} order={o} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {data.pages > 1 && (
            <div className="row between mt-16">
              <span className="faint" style={{ fontSize: 12.5 }}>
                Page {data.page} of {data.pages}
              </span>
              <div className="row gap-6">
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </button>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={page >= data.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {showCreate && <CreateOrder onClose={() => setParams({})} />}
    </>
  );
}

function OrderRow({ order: o }: { order: OrderSummary }) {
  const navigate = useNavigate();
  const progress = o.approval_progress;
  return (
    <tr
      className="clickable"
      onClick={() => navigate(`/orders/${o.id}`)}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/orders/${o.id}`); }}
    >
      <td><span className="vno">{o.voucher_no}</span></td>
      <td className="hide-mobile">{titleise(o.purpose)}</td>
      <td className="hide-mobile faint" style={{ fontSize: 12.5 }}>{formatDate(o.value_date)}</td>
      <td className={`num ${o.txn_type === 'DEBIT' ? 'debit' : 'credit'}`}>{o.total.formatted}</td>
      <td className="hide-mobile mono faint" style={{ fontSize: 12 }}>{o.line_count}</td>
      <td className="hide-mobile">
        {progress ? (
          <div className="row gap-6">
            <div style={{ width: 46, height: 4, background: 'var(--ink)', borderRadius: 3 }}>
              <div
                style={{
                  width: `${progress.percent}%`, height: '100%',
                  background: progress.percent === 100 ? 'var(--verify)' : 'var(--seal)',
                  borderRadius: 3,
                }}
              />
            </div>
            <span className="mono ghost" style={{ fontSize: 11 }}>
              {progress.approved_steps}/{progress.total_steps}
            </span>
          </div>
        ) : (
          <span className="ghost" style={{ fontSize: 12 }}>—</span>
        )}
      </td>
      <td><StatePill state={o.state} /></td>
      <td className="hide-mobile">{o.risk && o.risk.count > 0 ? <RiskPill level={o.risk.level} count={o.risk.count} /> : null}</td>
      <td className="hide-mobile ghost" style={{ fontSize: 11.5 }}>{relativeTime(o.updated_at)}</td>
    </tr>
  );
}

/* ── Create ────────────────────────────────────────────────────────────── */

interface DraftLine {
  key: number;
  beneficiary_id: string;
  amount: string;
  payment_mode: string;
  source_ref: string;
  description: string;
}

const newLine = (): DraftLine => ({
  key: Date.now() + Math.random(),
  beneficiary_id: '',
  amount: '',
  payment_mode: 'NEFT',
  source_ref: '',
  description: '',
});

function CreateOrder({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: banks } = useQuery({
    queryKey: ['banks'],
    queryFn: () => api.get<BankAccount[]>('/reference/bank-accounts'),
  });
  const { data: beneficiaries } = useQuery({
    queryKey: ['beneficiaries'],
    queryFn: () => api.get<Beneficiary[]>('/reference/beneficiaries?limit=500'),
  });

  const [purpose, setPurpose] = useState('PURCHASE');
  const [valueDate, setValueDate] = useState(new Date().toISOString().slice(0, 10));
  const [bankId, setBankId] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);

  const bank = bankId || banks?.[0]?.id || '';

  const total = lines.reduce((sum, l) => {
    const n = Number(l.amount.replace(/[^\d.]/g, ''));
    return sum + (Number.isFinite(n) ? Math.round(n * 100) : 0);
  }, 0);

  const create = useMutation({
    mutationFn: () =>
      api.post<OrderDetail>('/orders', {
        purpose,
        value_date: valueDate,
        bank_account_id: bank,
        note: note || null,
        lines: lines
          .filter((l) => l.beneficiary_id && l.amount)
          .map((l) => ({
            beneficiary_id: l.beneficiary_id,
            amount_minor: Math.round(Number(l.amount.replace(/[^\d.]/g, '')) * 100),
            payment_mode: l.payment_mode,
            source_ref: l.source_ref || null,
            description: l.description || null,
          })),
      }),
    onSuccess: (order) => {
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast(`${order.voucher_no} created`, 'ok');
      navigate(`/orders/${order.id}`);
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Could not create the order', 'bad'),
  });

  const valid = bank && lines.some((l) => l.beneficiary_id && Number(l.amount) > 0);

  const patch = (key: number, field: keyof DraftLine, value: string) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));

  return (
    <Modal title="New payment order" onClose={onClose} wide>
      <div className="col gap-14">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))' }}>
          <div className="field">
            <label className="label" htmlFor="purpose">Purpose</label>
            <select
              id="purpose" className="select" value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
            >
              {PURPOSES.map((p) => <option key={p} value={p}>{titleise(p)}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="label" htmlFor="vdate">Value date</label>
            <input
              id="vdate" type="date" className="input"
              value={valueDate} onChange={(e) => setValueDate(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="bank">Funding account</label>
            <select
              id="bank" className="select" value={bank}
              onChange={(e) => setBankId(e.target.value)}
            >
              {banks?.map((b) => (
                <option key={b.id} value={b.id}>{b.label} ····{b.account_last4}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <label className="label" htmlFor="note">Note</label>
          <input
            id="note" className="input" placeholder="What is this payment for?"
            value={note} onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div className="hint" style={{ borderStyle: 'solid' }}>
          Transaction type is set by the purpose, not chosen. {titleise(purpose)} posts as{' '}
          <strong style={{ color: ['DISINVESTMENT', 'CASH_RECEIVE'].includes(purpose) ? 'var(--verify)' : 'var(--breach)' }}>
            {['DISINVESTMENT', 'CASH_RECEIVE'].includes(purpose) ? 'credit' : 'debit'}
          </strong>.
        </div>

        <div>
          <div className="row between" style={{ marginBottom: 8 }}>
            <span className="label">Lines</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setLines((p) => [...p, newLine()])}>
              <PlusIcon /> Add line
            </button>
          </div>

          <div className="col gap-6">
            {lines.map((l) => (
              <div
                key={l.key}
                className="row gap-6 row-wrap"
                style={{ background: 'var(--ink)', padding: 9, borderRadius: 8 }}
              >
                <select
                  className="select grow" style={{ minWidth: 150 }}
                  value={l.beneficiary_id}
                  onChange={(e) => patch(l.key, 'beneficiary_id', e.target.value)}
                  aria-label="Beneficiary"
                >
                  <option value="">Select beneficiary…</option>
                  {beneficiaries?.map((b) => (
                    <option key={b.id} value={b.id}>{b.name} ({b.code})</option>
                  ))}
                </select>
                <input
                  className="input num" style={{ width: 116 }} placeholder="0.00" inputMode="decimal"
                  value={l.amount} onChange={(e) => patch(l.key, 'amount', e.target.value)}
                  aria-label="Amount"
                />
                <select
                  className="select" style={{ width: 104 }}
                  value={l.payment_mode}
                  onChange={(e) => patch(l.key, 'payment_mode', e.target.value)}
                  aria-label="Payment mode"
                >
                  {['NEFT', 'RTGS', 'UPI', 'IMPS', 'CASH', 'CHEQUE'].map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <input
                  className="input" style={{ width: 128 }} placeholder="Reference"
                  value={l.source_ref} onChange={(e) => patch(l.key, 'source_ref', e.target.value)}
                  aria-label="Reference"
                />
                <button
                  className="btn btn-bad btn-sm"
                  onClick={() => setLines((p) => (p.length > 1 ? p.filter((x) => x.key !== l.key) : p))}
                  aria-label="Remove line"
                >
                  −
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="row between" style={{ paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <div>
            <div className="label">Total</div>
            <div className="mono" style={{ fontSize: 21, fontWeight: 700 }}>
              {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(total / 100)}
            </div>
          </div>
          <div className="row gap-6">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-primary"
              disabled={!valid || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Creating…' : 'Create order'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
